import { ClipboardTextIcon } from "@phosphor-icons/react/dist/csr/ClipboardText";
import { PhoneIcon } from "@phosphor-icons/react/dist/csr/Phone";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Button } from "@volca/ui/components/ui/button";
import { Item, ItemActions, ItemContent } from "@volca/ui/components/ui/item";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import { cn } from "@volca/ui/lib/utils";
import {
  formatPhoneNumber,
  normalizePhoneWithPlusPrefix,
} from "@/core/utils/phone";

interface ContactInfoItemProps {
  label: string;
  value: string;
  onCopy: () => void;
  copied: boolean;
  variant?: "default" | "compact";
  iconSize?: "sm" | "xs";
}

const ContactInfoItem = ({
  label,
  value,
  onCopy,
  copied,
  variant = "default",
  iconSize = "sm",
}: ContactInfoItemProps) => (
  <Item
    variant={variant === "default" ? "outline" : "default"}
    size="sm"
    className={cn("flex-nowrap", {
      "bg-surface": variant === "default",
      "p-0": variant !== "default",
    })}
  >
    <ItemContent>
      <Text
        variant={variant === "default" ? "title" : "caption"}
        className={variant === "default" ? "text-xs text-subtle" : ""}
      >
        {variant === "default" ? (
          label
        ) : (
          <>
            <span className="font-medium">{label}:</span> {value}
          </>
        )}
      </Text>
      {variant === "default" && <Text>{value}</Text>}
    </ItemContent>
    <ItemActions>
      <Button
        variant="ghost"
        size="sm"
        onClick={onCopy}
        aria-label={variant === "default" ? undefined : `Copy ${label}`}
      >
        <ClipboardTextIcon
          className={iconSize === "sm" ? "h-4 w-4" : "h-3 w-3"}
        />
        {variant === "default" && (copied ? "Copied!" : "Copy")}
      </Button>
    </ItemActions>
  </Item>
);

export default function AdvocateSignupRedirect() {
  const [searchParams] = useSearchParams();
  const [shouldAutoRedirect, setShouldAutoRedirect] = useState(false);
  const [showFallback, setShowFallback] = useState(false);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [copied, setCopied] = useState<"phone" | "message" | null>(null);

  const phoneNumber = searchParams.get("phone") || "";
  const referralCode = searchParams.get("code") || "";

  const smsPhone = useMemo(
    () => normalizePhoneWithPlusPrefix(phoneNumber),
    [phoneNumber],
  );

  const displayPhone = useMemo(
    () => formatPhoneNumber(smsPhone, { withCountryCode: true }) || smsPhone,
    [smsPhone],
  );

  const smsBody = useMemo(() => `Referral${referralCode}`, [referralCode]);
  const smsLink = useMemo(
    () => `sms:${smsPhone}?body=${encodeURIComponent(smsBody)}`,
    [smsPhone, smsBody],
  );

  const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);
  const isAndroid = /Android/i.test(navigator.userAgent);
  const isMobile = isIOS || isAndroid;

  const isStandalone = window.matchMedia("(display-mode: standalone)").matches;

  const handleSmsClick = useCallback(() => {
    setIsRedirecting(true);

    // Use setTimeout to detect if user cancelled the redirect
    const redirectTimer = setTimeout(() => {
      // If we reach here, likely means user cancelled or SMS app didn't open
      setIsRedirecting(false);
      setShowFallback(true);
    }, 1000);

    // Attempt the redirect
    try {
      window.location.href = smsLink;

      // If successful, clear the timer and we don't need to show fallback
      window.addEventListener("beforeunload", () => {
        clearTimeout(redirectTimer);
      });

      // Also check if window loses focus (app opened successfully)
      const handleBlur = () => {
        clearTimeout(redirectTimer);
        window.removeEventListener("blur", handleBlur);
      };

      window.addEventListener("blur", handleBlur);

      // Cleanup after 3 seconds regardless
      setTimeout(() => {
        clearTimeout(redirectTimer);
        window.removeEventListener("blur", handleBlur);
      }, 3000);
    } catch {
      clearTimeout(redirectTimer);
      setIsRedirecting(false);
      setShowFallback(true);
    }
  }, [smsLink]);

  useEffect(() => {
    if (isMobile && !isStandalone && phoneNumber && referralCode) {
      setShouldAutoRedirect(true);
      // Redirect immediately when data is available
      handleSmsClick();
    }
  }, [
    handleSmsClick,
    isMobile,
    isStandalone,
    phoneNumber,
    referralCode,
    smsLink,
  ]);

  const copyToClipboard = async (text: string, type: "phone" | "message") => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(type);
      setTimeout(() => setCopied(null), 2000);
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  };

  if (!phoneNumber || !referralCode) {
    return (
      <Stack
        gap={4}
        align="center"
        justify="center"
        className="h-full w-full p-4"
      >
        <Stack gap={4} align="center">
          <Text variant="display" className="text-center">
            Invalid link
          </Text>
          <Text tone="muted">
            This referral link is missing required information.
          </Text>
        </Stack>
      </Stack>
    );
  }

  return (
    <Stack
      gap={4}
      align="center"
      justify="center"
      className="h-full w-full p-4"
    >
      <Stack gap={6} align="center" className="w-full max-w-md">
        <Stack gap={4} align="center">
          <Text variant="display" className="text-center">
            Join with referral code
          </Text>
          {isRedirecting ? (
            <Text className="text-center text-subtle">
              Opening your messaging app...
            </Text>
          ) : shouldAutoRedirect && !showFallback ? (
            <Text className="text-center text-subtle">
              Opening your messaging app...
            </Text>
          ) : (
            <Text className="text-center text-subtle">
              {isMobile
                ? "Tap the button below to send your referral code"
                : "Send a text message to complete your referral sign-up"}
            </Text>
          )}
        </Stack>

        {isMobile && (showFallback || !shouldAutoRedirect) ? (
          <Stack className="w-full" gap={4} align="center">
            <Button
              onClick={handleSmsClick}
              size="lg"
              className="w-full max-w-xs"
              disabled={isRedirecting}
              loading={isRedirecting}
            >
              {!isRedirecting && (
                <PhoneIcon className="h-5 w-5" data-icon="inline-start" />
              )}
              Send referral code
            </Button>
          </Stack>
        ) : !isMobile ? (
          <Stack
            align="start"
            gap={6}
            className="w-full rounded-lg bg-surface-sunken p-6 pt-0"
          >
            <Stack className="w-full" align="start" gap={2}>
              <ContactInfoItem
                label="Phone Number"
                value={displayPhone}
                onCopy={() => copyToClipboard(smsPhone, "phone")}
                copied={copied === "phone"}
                variant="default"
                iconSize="sm"
              />
              <ContactInfoItem
                label="Message"
                value={smsBody}
                onCopy={() => copyToClipboard(smsBody, "message")}
                copied={copied === "message"}
                variant="default"
                iconSize="sm"
              />
            </Stack>
            <Stack className="w-full" align="start" gap={2}>
              <Button
                onClick={handleSmsClick}
                variant="secondary"
                className="w-full"
              >
                <PhoneIcon className="mr-2 h-4 w-4" />
                Try SMS Link Anyway
              </Button>
              <Text variant="caption" className="text-center text-subtle">
                SMS links may not work on all desktop browsers
              </Text>
            </Stack>
          </Stack>
        ) : null}

        {isMobile && showFallback && (
          <Stack
            gap={2}
            align="center"
            className="rounded-lg bg-warning-subtle p-4"
          >
            <Text variant="caption" className="text-warning">
              Didn't work? Try copying the details manually:
            </Text>
            <Stack align="start" gap={1} className="w-full">
              <ContactInfoItem
                label="Phone"
                value={displayPhone}
                onCopy={() => copyToClipboard(smsPhone, "phone")}
                copied={copied === "phone"}
                variant="compact"
                iconSize="xs"
              />
              <ContactInfoItem
                label="Message"
                value={smsBody}
                onCopy={() => copyToClipboard(smsBody, "message")}
                copied={copied === "message"}
                variant="compact"
                iconSize="xs"
              />
            </Stack>
          </Stack>
        )}
      </Stack>
    </Stack>
  );
}
